import {
  createAsyncThunk, createSlice, EntityState, PayloadAction,
} from '@reduxjs/toolkit';
import { putLayoutCopyTo } from 'api/layout.service';
import { projectReadActions } from 'redux/project-read/project-read.slice';
import { LayoutCopyRequest, LayoutResponse, ProjectIdLayoutIdPair } from 'types/entity';
import { layoutCopyUpdateAdapter } from 'redux/layout-copy-update/layout-copy-update.adapter';
import { notificationActions } from 'redux/notification/notification.slice';

export const LAYOUT_COPY_UPDATE_STORE_NAME = 'layoutCopyUpdate';

export const copyLayout = createAsyncThunk(
  `${LAYOUT_COPY_UPDATE_STORE_NAME}/update`,
  async (payload: { projectId: string, sourceLayout: LayoutResponse, targetList: LayoutCopyRequest }, thunkAPI) => {
    const {
      projectId,
      sourceLayout,
      targetList,
    } = payload;
    await putLayoutCopyTo(projectId, sourceLayout.layout_id || '', targetList)
      .then(() => {
        thunkAPI.dispatch(notificationActions.addSuccess({ messageId: 'FpLayoutCopy.notification.success' }));
      });
    return sourceLayout;
  },
);

export type LayoutCopyUpdateState = EntityState<ProjectIdLayoutIdPair> & {
  sourceLayoutId: string
  updated: boolean
  error?: any
}

export const initialLayoutCopyUpdateState: LayoutCopyUpdateState = layoutCopyUpdateAdapter.getInitialState({
  sourceLayoutId: '',
  updated: false,
  error: null,
});

export const layoutCopyUpdateSlice = createSlice({
  name: LAYOUT_COPY_UPDATE_STORE_NAME,
  initialState: initialLayoutCopyUpdateState,
  // awaitでないAction系
  reducers: {
    add: (state, action: PayloadAction<ProjectIdLayoutIdPair>) => layoutCopyUpdateAdapter.upsertOne(state, action.payload),
    remove: (state, action: PayloadAction<ProjectIdLayoutIdPair>) => {
      const {
        removeOne,
        selectId,
      } = layoutCopyUpdateAdapter;
      return removeOne(state, selectId(action.payload));
    },
    clear: (state) => layoutCopyUpdateAdapter.removeAll(state),
  },
  // awaitするAction系
  extraReducers: (builder) => {
    builder.addCase(copyLayout.pending, (state) => {
      state.sourceLayoutId = '';
      state.updated = false;
      state.error = null;
    });
    builder.addCase(copyLayout.fulfilled, (state, action) => {
      layoutCopyUpdateAdapter.removeAll(state);
      state.sourceLayoutId = action.payload.layout_id || '';
      state.updated = true;
      state.error = null;
    });
    builder.addCase(copyLayout.rejected, (state, action) => {
      state.sourceLayoutId = '';
      state.updated = true;
      state.error = action.payload;
    });

    // プロジェクト切り替え時
    builder.addCase(projectReadActions.selectOne, (state) => {
      layoutCopyUpdateAdapter.removeAll(state);
      state.updated = false;
      state.error = null;
    });
  },
});

// Action creators are generated for each case reducer function
export const layoutCopyUpdateActions = {
  ...layoutCopyUpdateSlice.actions,
  copyLayout,
};

export default layoutCopyUpdateSlice.reducer;
